import { THEME_ID, type ThemeId } from '../../../themes/types';

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FaqPageCopy {
  subtitle: string;
  items: readonly FaqItem[];
}

const KINDLY_FAQ: FaqPageCopy = {
  subtitle: 'Everything you need to know about Kindly Human',
  items: [
    {
      question: 'What is Kindly Human?',
      answer:
        'Kindly Human offers Peer support that is accessible 24/7 from the convenience of your desktop or mobile device. You can listen to Peer stories, and have anonymous one-on-one calls with Peer Listeners about everyday challenges and situations. Additional support pathways through resources and benefits are also matched to your support searches.',
    },
    {
      question: 'How do I create a Kindly Human member account?',
      answer:
        '■ Visit kindlyhuman.io and click on "Get Started" to activate your account.\n■ Enter your email address and create a password.\n■ Follow the on-screen instructions to complete your profile setup.',
    },
    {
      question: 'What if I encounter issues during the sign-up process?',
      answer: "Contact Kindly Human's support team via email at questions@kindlyhuman.com.",
    },
    {
      question: 'How can I update my account information?',
      answer: 'Once logged in, navigate to the "My Program" tile where you can update personal information.',
    },
    {
      question: 'How do I utilize the Kindly Human services once my account is created?',
      answer:
        'After logging into your account, you will be able to type a keyword or challenge area into the search bar and we will match you with related Peers and resources. You can listen to Peer audio stories, schedule a call, or explore resources.',
    },
    {
      question: 'When are listeners available?',
      answer:
        'Listeners are available 24/7, so you can reach out whenever you need support. We aim to have listeners ready to connect with you at any time.',
    },
    {
      question: 'Who are Kindly Human Peers?',
      answer:
        'A Peer is someone who has lived through various life experiences, similar to those you may be facing. They are individuals from different backgrounds who offer encouragement, validation, and a fresh perspective based on their own journeys.',
    },
    {
      question: 'Who are the Peer Listeners?',
      answer:
        'Kindly Human Peer Listeners are trained individuals who undergo a rigorous selection and training process. They provide empathy and understanding through one-on-one conversations but are not licensed practitioners.',
    },
    {
      question: 'Is my conversation private and confidential?',
      answer:
        'Yes, your privacy is our top priority. All conversations are confidential, and we ensure that your personal information is protected according to our privacy policy.',
    },
    {
      question: 'What happens on a Kindly Human call?',
      answer:
        "When you connect with one of our trained Peers on a Kindly Human call, they will start by simply asking how they can help you today. Your Peer will create a safe and supportive space for you to share what's on your mind.\nDuring the conversation, the Peer may share their own life experiences and how they handled a similar situation. This helps to create a deeper connection and lets you know you are not alone in what you're going through. The focus is always on listening empathetically, offering support, and helping you feel heard and understood.",
    },
    {
      question: 'What resources and additional support tools are available within Kindly Human?',
      answer:
        'When you search topics we will not only surface related Peers, but we will also match you with national resources and support tools that you have through your organization.',
    },
  ],
};

const KINDLY_CHRISTIAN_FAQ: FaqPageCopy = {
  subtitle: 'Everything you need to know about Kindly Christian',
  items: [
    {
      question: 'What is Kindly Christian?',
      answer:
        'Kindly Christian offers faith-centered peer support that is accessible 24/7 from your desktop or mobile device. You can listen to stories from believers, connect one-on-one with Peer Listeners, and explore encouragement and resources grounded in shared faith and lived experience.',
    },
    {
      question: 'How do I create a Kindly Christian account?',
      answer:
        'Visit your Kindly Christian access page and click Sign Up or Register. Enter your email address, create a password, and follow the on-screen steps to complete your profile.',
    },
    {
      question: 'Is there an app for Kindly Christian?',
      answer:
        'Kindly Christian is available through any web browser on desktop or mobile, making it easy to access wherever you are.',
    },
    {
      question: 'What information do I need to set up my account?',
      answer:
        'You will need your email address and a password. You may also be asked for basic personal information, such as your name and date of birth, to complete account activation.',
    },
    {
      question: 'What if I forgot my password?',
      answer:
        'Click the Forgot Password link on the login page, enter your email address, and follow the instructions sent to you to reset your password.',
    },
    {
      question: 'What if I have trouble signing up?',
      answer: 'If you run into issues during sign-up, please contact support for help.',
    },
    {
      question: 'How do I update my account information?',
      answer: 'Once logged in, navigate to your profile or program area to update your personal information.',
    },
    {
      question: 'How do I use Kindly Christian once my account is created?',
      answer:
        'After logging in, you can search by topic, life challenge, or area where you would like support. Kindly Christian will surface related Peer stories, Peer Listeners, and additional resources for you to explore.',
    },
    {
      question: 'Who are Kindly Christian Peers?',
      answer:
        'Peers are individuals who have walked through real-life experiences and are willing to share encouragement, hope, and perspective with others. In Kindly Christian, these are believers offering support through shared faith and lived understanding.',
    },
    {
      question: 'Who are the Peer Listeners?',
      answer:
        'Peer Listeners are trained individuals who provide one-on-one support through compassionate conversation. They are not licensed clinicians, but they are equipped to listen with empathy, care, and understanding.',
    },
    {
      question: 'Is my conversation private and confidential?',
      answer:
        'Yes. Your privacy is a priority, and conversations are handled with care and confidentiality in accordance with the platform’s privacy practices.',
    },
    {
      question: 'Is Kindly Christian counseling or pastoral care?',
      answer:
        'No. Kindly Christian provides peer connection, not clinical care, counseling, or pastoral advising.\nPeers:\n• Do not diagnose\n• Do not provide therapy\n• Do not provide theological instruction\n• Do not replace pastors or counselors\nInstead, they offer empathetic listening rooted in shared experience and faith-informed understanding. When appropriate, we can help build awareness of pastoral, clinical, or crisis resources.',
    },
    {
      question: 'Can I choose a specific listener?',
      answer:
        'Yes. When you search for a topic or area of support, you will see Peer Listeners who align with that need. You can review availability and choose the person and time that works best for you.',
    },
    {
      question: 'What happens during a Kindly Christian call?',
      answer:
        'A Kindly Christian call is a supportive conversation centered on listening, encouragement, and understanding. Your Peer Listener will invite you to share what is on your heart and may offer perspective from their own lived experience. The goal is to help you feel seen, supported, and not alone.',
    },
    {
      question: 'When are listeners available?',
      answer: 'Kindly Christian offers access to support 24/7, so you can seek connection whenever you need it.',
    },
    {
      question: 'How is talking to a Peer Listener different from counseling?',
      answer:
        'Kindly Christian offers non-clinical, faith-centered peer support. Peer Listeners do not diagnose, treat, or provide therapy. Instead, they offer encouragement and understanding from a lived-experience perspective.',
    },
    {
      question: 'Why should I talk to a Peer Listener?',
      answer:
        'Talking with someone who understands can bring comfort, perspective, and reassurance. It can help you feel less alone and remind you that support, faith, and connection are available in the midst of life’s challenges.',
    },
    {
      question: 'What topics can I talk about?',
      answer:
        'You can talk about many everyday life experiences, including stress, relationships, family, work, purpose, grief, loneliness, personal growth, and faith-related challenges.',
    },
    {
      question: 'What if I am unhappy with my conversation experience?',
      answer:
        'If a conversation does not feel like the right fit, you can share feedback through the platform or contact support directly. Your experience matters, and feedback helps improve future connections.',
    },
    {
      question: 'What resources are available within Kindly Christian?',
      answer:
        'In addition to Peer stories and one-on-one conversations, Kindly Christian can surface helpful resources connected to your search, including faith-centered encouragement and additional support tools available through your organization.',
    },
    {
      question: 'Is Kindly Christian aligned to a specific denomination or church tradition?',
      answer:
        'No. Kindly Christian is not designed around any single denomination, doctrine, or church affiliation. The experience focuses on shared human experiences through a Christian lens, grounded in empathy, listening, and connection.',
    },
    {
      question: 'Can I access Kindly Christian support at any time?',
      answer:
        'Yes. Kindly Christian provides 24/7 access to stories, listeners, and resources so support is available whenever you need it.',
    },
    {
      question: 'What if I have more questions?',
      answer: 'If you have additional questions that are not covered here, please contact support for assistance.',
    },
  ],
};

const BY_THEME: Record<ThemeId, FaqPageCopy> = {
  [THEME_ID.KINDLY]: KINDLY_FAQ,
  [THEME_ID.KINDLY_CHRISTIAN]: KINDLY_CHRISTIAN_FAQ,
};

export function getFaqPageCopy(themeId: ThemeId): FaqPageCopy {
  return BY_THEME[themeId];
}
